import type { TravelOption } from './data';

export type TravelKind = 'flight' | 'hotel' | 'train';

export interface TravelChoice extends Record<string, unknown> {
  choiceId: string;
  choiceTitle?: string;
  returnFor?: string;
  fromPrice?: number;
  price_currency?: string;
  priceCurrency?: string;
  flightOffer?: Record<string, unknown>;
  hotelOffer?: Record<string, unknown>;
  railOffer?: Record<string, unknown>;
}

export interface TravelCard {
  id: string;
  artifactId: string;
  artifactTitle: string;
  kind: TravelKind;
  title: string;
  price?: number;
  currency?: string;
  choice: TravelChoice;
}

export interface TravelArtifactChoices {
  id: string;
  title?: string;
  choices?: unknown;
}

export type SortKey =
  | 'recommended'
  | 'price'
  | 'dep'
  | 'arr'
  | 'time'
  | 'rating';

export const TRANSPORT_SORTS: Array<[SortKey, string]> = [
  ['recommended', 'travelOptions.sort.recommended'],
  ['price', 'travelOptions.sort.price'],
  ['dep', 'travelOptions.sort.departure'],
  ['arr', 'travelOptions.sort.arrival'],
  ['time', 'travelOptions.sort.duration'],
];

export const STAY_SORTS: Array<[SortKey, string]> = [
  ['recommended', 'travelOptions.sort.recommended'],
  ['price', 'travelOptions.sort.price'],
  ['rating', 'travelOptions.sort.rating'],
];

export const RETURN_CHOICES_MAX_AGE_MS = 10 * 60 * 1000;

export function hasStaleReturnChoices(
  outboundChoiceId: string,
  choices: unknown[],
  now = Date.now(),
): boolean {
  return choices.some((choice) => {
    const row = asRecord(choice);
    if (
      row?.returnFor !== outboundChoiceId ||
      typeof row.returnsFetchedAt !== 'string'
    ) {
      return false;
    }
    const fetchedAt = Date.parse(row.returnsFetchedAt);
    return (
      Number.isFinite(fetchedAt) && now - fetchedAt > RETURN_CHOICES_MAX_AGE_MS
    );
  });
}

export function sorted<T extends TravelOption>(list: T[], key: SortKey): T[] {
  if (key === 'recommended') return [...list];
  const compare = (left: T, right: T) => {
    if (key === 'price') return left.price - right.price;
    if (key === 'rating') {
      const rating = (option: T) =>
        option.kind === 'hotel' && option.rating?.outOf
          ? option.rating.score / option.rating.outOf
          : 0;
      return rating(right) - rating(left);
    }
    const transport = (option: TravelOption) =>
      option.kind === 'hotel' ? null : option;
    const leftTransport = transport(left);
    const rightTransport = transport(right);
    if (key === 'dep' || key === 'arr') {
      const field = key === 'dep' ? 'dep' : 'arr';
      const leftTime = leftTransport?.legs[0]?.[field];
      const rightTime = rightTransport?.legs[0]?.[field];
      if (!leftTime || !rightTime) {
        return leftTime ? -1 : rightTime ? 1 : 0;
      }
      return leftTime.localeCompare(rightTime);
    }
    const duration = (option: TravelOption) =>
      option.kind === 'hotel'
        ? 0
        : option.legs.reduce((total, leg) => total + leg.durationMin, 0);
    return duration(left) - duration(right);
  };
  return [...list].sort(compare);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function numeric(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function firstCurrency(...values: unknown[]): string | undefined {
  return values.find((value): value is string => typeof value === 'string');
}

export function adaptTravelChoices(
  artifact: TravelArtifactChoices,
  options: { includeReturnChoices?: boolean } = {},
): TravelCard[] {
  if (!Array.isArray(artifact.choices)) return [];
  return artifact.choices.flatMap((raw): TravelCard[] => {
    const row = asRecord(raw);
    if (
      !row ||
      typeof row.choiceId !== 'string' ||
      (row.returnFor && !options.includeReturnChoices)
    )
      return [];

    const flightOffer = asRecord(row.flightOffer);
    const hotelOffer = asRecord(row.hotelOffer);
    const railOffer = asRecord(row.railOffer);
    const kind: TravelKind | null = railOffer
      ? 'train'
      : hotelOffer
        ? 'hotel'
        : flightOffer
          ? 'flight'
          : null;
    if (!kind) return [];

    const fare = asRecord(
      Array.isArray(flightOffer?.fares) ? flightOffer?.fares?.[0] : undefined,
    );
    const source = asRecord(
      Array.isArray(flightOffer?.sources)
        ? flightOffer?.sources?.[0]
        : undefined,
    );
    const room = asRecord(
      Array.isArray(hotelOffer?.rooms) ? hotelOffer?.rooms?.[0] : undefined,
    );
    const rate = asRecord(
      Array.isArray(room?.rates) ? room?.rates?.[0] : undefined,
    );
    const title =
      typeof row.choiceTitle === 'string' ? row.choiceTitle : 'Travel option';
    return [
      {
        id: row.choiceId,
        artifactId: artifact.id,
        artifactTitle: artifact.title ?? '',
        kind,
        title,
        price:
          numeric(row.fromPrice) ??
          numeric(railOffer?.price) ??
          numeric(fare?.total) ??
          numeric(source?.total) ??
          numeric(rate?.total),
        currency: firstCurrency(
          row.price_currency,
          row.priceCurrency,
          railOffer?.currency,
          fare?.currency,
          source?.currency,
          rate?.currency,
        ),
        choice: row as TravelChoice,
      },
    ];
  });
}

export function groupTravelCards(cards: TravelCard[]): {
  flight: TravelCard[];
  hotel: TravelCard[];
  train: TravelCard[];
} {
  return {
    flight: cards.filter((card) => card.kind === 'flight'),
    hotel: cards.filter((card) => card.kind === 'hotel'),
    train: cards.filter((card) => card.kind === 'train'),
  };
}

export function hasTravelTabs(
  groups: Record<TravelKind, TravelCard[]>,
): boolean {
  return Object.values(groups).filter((cards) => cards.length > 0).length > 1;
}

export function pinnedTravelChoice(
  cards: TravelCard[],
  choiceId: string | null,
): string | null {
  return choiceId && cards.some((card) => card.id === choiceId)
    ? choiceId
    : null;
}

export function shouldShowTravelSkeletons(
  relatedLoading: boolean,
  returnsLoading: boolean,
): boolean {
  return relatedLoading || returnsLoading;
}
